import { useState } from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import { Text, theme } from '@socrait/react';

import { ReviewDayStatus } from '~/services/types';
import { useSessionRequestCloseMutation } from '~/services/session';

export type ProcessingBannerProps = {
  status: ReviewDayStatus | null;
  scheduledCloseAt?: string;
};

function formatScheduledTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export const ProcessingBanner = ({ status, scheduledCloseAt }: ProcessingBannerProps) => {
  const [justClicked, setJustClicked] = useState(false);
  const { mutate: requestClose } = useSessionRequestCloseMutation();

  // Flip the banner to "processing" the instant the CTA is clicked — the click always
  // succeeds from the teacher's perspective, regardless of the mutation's outcome. The
  // invalidated review queries reconcile with the real status shortly after.
  const displayedStatus = status === 'idle' && justClicked ? 'processing' : status;

  if (!displayedStatus || displayedStatus === 'processed') {
    return null;
  }

  const handleRequestClose = () => {
    setJustClicked(true);
    requestClose();
  };

  if (displayedStatus === 'idle') {
    return (
      <View style={styles.container}>
        <Text style={styles.text}>
          {`Processing pending — scheduled for ${scheduledCloseAt ? formatScheduledTime(scheduledCloseAt) : 'soon'}. `}
        </Text>
        <Pressable onPress={handleRequestClose}>
          <Text style={[styles.text, styles.link]}>
            Click here if you are done teaching for the day, and we'll process immediately.
          </Text>
        </Pressable>
      </View>
    );
  }

  let text: string;
  switch (displayedStatus) {
    case 'active':
      text = 'Class is streaming now.';
      break;
    case 'processing':
      text = 'Processing your class content…';
      break;
    case 'failed':
      text = "Something went wrong processing today's class, data may be incomplete.";
      break;
    default:
      // The banner should only be shown for active/idle/processing/failed statuses.
      console.error('Invalid review day status', displayedStatus);
      text = '';
      break;
  }

  return (
    <View style={styles.container}>
      <Text style={styles.text}>{text}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    backgroundColor: theme.colors.primaryPurple,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    padding: theme.spacing.large,
    width: '100%',
  },
  text: {
    color: theme.colors.white,
    fontSize: theme.fontSizes.small,
  },
  link: {
    textDecorationLine: 'underline',
    fontWeight: theme.fontWeights.bold,
  },
});
